// A süti-hozzájárulás FELÜLETE.
//
// A döntés logikája külön él (`lib/consent.ts`), és nem ismeri a DOM-ot —
// így böngésző nélkül is mérhető. Ez a fájl csak megjeleníti.
//
// ALVÓ ÁLLAPOT. A banner CSAK akkor jelenik meg, ha van bekötött, hozzá-
// járuláshoz kötött eszköz. Ma nincs: az app.blazedata.hu kijelentkezve
// semmilyen külső kódot nem tölt be. Hozzájárulást kérni olyasmire, ami nem
// történik, félrevezető — és pont az a banner égeti el a látogató bizalmát,
// amelyik semmit nem véd.
//
// Ugyanez igaz a lábléc „Süti-beállítások" hivatkozására: eszköz nélkül
// nincs mit beállítani, ezért az sem jelenik meg.

import { h, icon, icons } from '../lib/dom';
import {
  beolvasDontes,
  kellKerdezni,
  mentDontes,
  vanKapuzottEszkoz,
} from '../lib/consent';

const CIM = 'Te döntöd el, mit mérhetünk';

// RÖVID, mert telefonon a sáv a képernyő aljából nő felfelé: minden fölösleges
// sor a tartalom elől vesz el helyet. A részletes magyarázat a tájékoztatóban van.
const LEIRAS =
  'A működéshez szükségeseket mindig használjuk. Minden máshoz a te ' +
  'engedélyed kell — addig egyetlen külső kód sem töltődik be.';

type Kapcsolo = { elem: HTMLElement; ertek: () => boolean };

/// Egy kategória sora: kapcsolóval, és az EGÉSZ sor kattintható.
///
/// Korábban csupasz jelölőnégyzetek voltak egymás alatt, cím nélküli
/// szövegfolyamban — a doboz emiatt nézett ki befejezetlennek. A kapcsoló
/// (`.sw`) a dizájnrendszerben eleve megvan; itt csak használjuk.
function kapcsolo(
  cimke: string,
  leiras: string,
  allapot: boolean,
  zarolt = false,
): Kapcsolo {
  let be = allapot;

  const kapcs = h('span', {
    class: `sw${be ? ' on' : ''}`,
    role: 'switch',
    'aria-checked': String(be),
    'aria-label': cimke,
    ...(zarolt ? { 'aria-disabled': 'true' } : { tabindex: '0' }),
    style: zarolt ? { opacity: '.55', cursor: 'default' } : undefined,
  });

  const sor = h(
    'div',
    {
      class: 'row g12 sunken suti-kat',
      style: {
        alignItems: 'flex-start',
        padding: '12px 14px',
        cursor: zarolt ? 'default' : 'pointer',
        border: '1px solid var(--border)',
      },
    },
    h(
      'span',
      { class: 'col g4 grow' },
      h(
        'span',
        { class: 'row g8', style: { alignItems: 'center' } },
        h('span', { style: { fontWeight: '600', fontSize: '14px' }, text: cimke }),
        // A zárolt sor MEGMONDJA, miért nem kapcsolható. Enélkül úgy néz ki,
        // mintha elromlott volna.
        zarolt
          ? h('span', {
              class: 'badge st-ok',
              style: { fontSize: '11px' },
              text: 'Mindig aktív',
            })
          : null,
      ),
      h('span', { class: 'caption ts', text: leiras }),
    ),
    kapcs,
  );

  if (!zarolt) {
    const valt = (): void => {
      be = !be;
      kapcs.classList.toggle('on', be);
      kapcs.setAttribute('aria-checked', String(be));
    };
    sor.addEventListener('click', valt);
    kapcs.addEventListener('keydown', (e: Event) => {
      const k = e as KeyboardEvent;
      if (k.key === ' ' || k.key === 'Enter') {
        k.preventDefault();
        e.stopPropagation();
        valt();
      }
    });
  }

  return { elem: sor, ertek: () => be };
}

/// A banner (vagy a beállítások) megnyitása.
///
/// `kenyszeritett`: a lábléc hivatkozásáról jön, tehát akkor is nyíljon meg,
/// ha a látogató már döntött — a visszavonás legyen olyan egyszerű, mint a
/// megadás volt.
export function sutiBanner(kenyszeritett = false): HTMLElement | null {
  if (!vanKapuzottEszkoz()) return null;
  if (!kenyszeritett && !kellKerdezni()) return null;

  const mostani = beolvasDontes();

  const szukseges = kapcsolo(
    'Szükséges',
    'A belépéshez és a munkamenethez kell.',
    true,
    true,
  );
  const statisztika = kapcsolo(
    'Statisztika',
    'Melyik oldal mennyire hasznos.',
    mostani?.statisztika ?? false,
  );
  const marketing = kapcsolo(
    'Marketing',
    'Hirdetéseink mérése és remarketing.',
    mostani?.marketing ?? false,
  );

  let doboz: HTMLElement;
  const bezar = (): void => (doboz.parentElement ?? doboz).remove();

  const gomb = (szoveg: string, osztaly: string, dontes: () => void): HTMLElement =>
    h('button', {
      class: `btn ${osztaly} grow`,
      style: { flexBasis: '0', minWidth: '140px' },
      type: 'button',
      text: szoveg,
      onClick: () => {
        dontes();
        bezar();
      },
    });

  doboz = h(
    'div',
    {
      class: 'card el3 col g16 suti-sav',
      role: 'dialog',
      'aria-modal': 'false',
      'aria-label': CIM,
      style: {
        // A POZICIONALAS A BURKOLATE, a kartya csak egy normal blokk benne.
        //
        // Eddig a kartya volt `position: fixed`, `left:16px; right:16px`
        // parossal. Telefonon (393 px) ebbol 416 px szeles lap lett: a lap
        // vizszintesen gorgethetove valt, es az egesz tartalom elcsuszott.
        // `left`/`right` helyett a burkolat feszul ki, a kartyat pedig a
        // `max-width` + `margin: 0 auto` tartja helyen — igy nincs mibol
        // tulnyulni.
        maxWidth: '560px',
        width: '100%',
        margin: '0 auto',
        padding: '22px',
        pointerEvents: 'auto',
        // Finom akcentus-csík a tetején: a doboz így nem csak egy szürke
        // téglalap, és a márka színe megjelenik anélkül, hogy a szöveg
        // olvashatóságát rontaná.
        borderTop: '3px solid var(--accent)',
        animation: 'blazeSutiBe .22s ease-out',
      },
    },
    h(
      'div',
      { class: 'row g12', style: { alignItems: 'flex-start' } },
      h(
        'span',
        {
          class: 'ic',
          style: {
            width: '38px',
            height: '38px',
            borderRadius: 'var(--r-sm)',
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
          },
        },
        icon(icons.shield, 20),
      ),
      h(
        'div',
        { class: 'col g4 grow' },
        h('span', { class: 'h3', text: CIM }),
        h('span', { class: 'caption ts', text: LEIRAS }),
      ),
    ),

    // A KATEGÓRIÁK ÖSSZECSUKVA INDULNAK.
    //
    // Nyitva a sáv telefonon a képernyő FELÉT elfoglalta (427 px a 852-ből),
    // és az alja le is vágódott. Aki csak dönteni akar, annak két gomb kell;
    // akit érdekel a részlet, egy koppintásra megkapja — a `details` ehhez
    // nem igényel JavaScriptet, és billentyűzetről is nyitható.
    //
    // A „Beállítások mentése" ide, a kapcsolók MELLÉ került: külön menteni
    // csak akkor van értelme, ha a látogató hozzá is nyúlt valamihez.
    h(
      'details',
      { class: 'suti-reszletek' },
      h('summary', { class: 'caption', text: 'Kategóriánként beállítom' }),
      h(
        'div',
        { class: 'col g8', style: { marginTop: '10px' } },
        szukseges.elem,
        statisztika.elem,
        marketing.elem,
        h('button', {
          class: 'btn btn-ghost btn-sm',
          style: { alignSelf: 'flex-start' },
          type: 'button',
          text: 'Beállítások mentése',
          onClick: () => {
            mentDontes({
              statisztika: statisztika.ertek(),
              marketing: marketing.ertek(),
            });
            bezar();
          },
        }),
      ),
    ),

    // AZ ELFOGADÁS ÉS AZ ELUTASÍTÁS AZONOS SÚLYÚ, és ezért egyik sem kap
    // kiemelt (`btn-primary`) megjelenést. Az első változatban az elfogadás
    // kitöltött, hangsúlyos gomb volt, az elutasítás pedig átlátszó kerettel
    // puszta szövegnek látszott — méréssel: `border-color: rgba(0,0,0,0)`.
    // Ha az elutasítás halványabb, a hozzájárulás nem önkéntes, akkor sem, ha
    // formailag ott a lehetőség.
    //
    // A „Beállítások mentése" szándékosan MÁS súlyú: az nem a kétféle válasz
    // egyike, hanem a részletes út — a `btn-ghost` ezt fejezi ki.
    h(
      'div',
      { class: 'row g8 wrap' },
      gomb('Elutasítom mind', 'btn-secondary', () =>
        mentDontes({ statisztika: false, marketing: false }),
      ),
      gomb('Elfogadom mind', 'btn-secondary', () =>
        mentDontes({ statisztika: true, marketing: true }),
      ),
    ),
    h(
      'div',
      { class: 'row between', style: { alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
      h(
        'span',
        { class: 'caption ts' },
        'Részletek: ',
        h('a', { href: '#/adatkezeles', text: 'Adatkezelési tájékoztató' }),
      ),
    ),
  );

  // A TOKENEKET A `.app` OSZTALY HORDOZZA, es az a `#root`-on ul. Ez a sav
  // viszont a `<body>` ala kerul — kulonben a `render()` minden
  // kepernyovaltasnal letorolne, mert az `replaceChildren`-nel dolgozik.
  //
  // Enelkul a burkolat nelkul EGYETLEN token sem oldodott fel: a doboz
  // hattere es szovegszine is ures maradt, igy atlatszo savkent ult a lapon,
  // a mogotte levo tartalommal osszefolyva. Ugyanez a hiba mar megtortent a
  // parbeszedablakoknal (`lib/dialog.ts`), es ott le is van irva — ugyanazt
  // a mintat kovetjuk.
  const gyoker = document.getElementById('root');
  return h(
    'div',
    {
      class: `app${gyoker?.classList.contains('dark') ? ' dark' : ''}`,
      style: {
        position: 'fixed',
        left: '0',
        right: '0',
        bottom: '0',
        padding: '16px',
        boxSizing: 'border-box',
        zIndex: '60',
        // A burkolat a teljes szelesseget lefedi, de nem foghatja el a
        // kattintasokat a sav MELLETT — kulonben a lap also savja
        // hasznalhatatlan lenne.
        pointerEvents: 'none',
      },
    },
    doboz,
  );
}

/// A lábléc „Süti-beállítások" hivatkozása.
///
/// `null`, ha nincs bekötött eszköz — eszköz nélkül nincs mit beállítani.
export function sutiBeallitasLink(): HTMLElement | null {
  if (!vanKapuzottEszkoz()) return null;
  return h('a', {
    class: 'caption',
    href: '#',
    text: 'Süti-beállítások',
    onClick: (e: Event) => {
      e.preventDefault();
      const b = sutiBanner(true);
      if (b) document.body.appendChild(b);
    },
  });
}

/// Beillesztés az oldal betöltésekor. Ha nincs mit kérdezni, nem csinál semmit.
export function sutiBannerMegjelenites(): void {
  const b = sutiBanner();
  if (b) document.body.appendChild(b);
}

